import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { COLORS } from "../../constants/Color";
import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Loading from "../Layout/Loading";

const SalesPieChart = ({ salesData }) => {
  const navigate = useNavigate();
  const [outerRadiusChart, setOuterRadiusChart] = useState(160);
  const [containerHeight, setContainerHeight] = useState(600);

  // Transform and process sales data for PieChart
  const processedPieData = React.useMemo(() => {
    if (!salesData || salesData.length === 0) return [];

    // Sort by sales in descending order
    const sortedData = [...salesData].sort((a, b) => b.sales - a.sales);

    // Take top 10 items
    const top10 = sortedData.slice(0, 10);
    const others = sortedData.slice(10);

    // Calculate total for "Others"
    const othersTotal = others.reduce((sum, item) => sum + item.sales, 0);

    // Create pie data array
    const pieData = top10.map((product) => ({
      name: product.name,
      value: product.sales,
    }));

    // Add "Others" if there are items beyond top 10
    if (others.length > 0) {
      pieData.push({
        name: "Others",
        value: othersTotal,
      });
    }

    return pieData;
  }, [salesData]);
  // State for responsive sizing


  // Transform sales data for PieChart
  const pieData =
    salesData?.map((product) => ({
      name: product.name,
      value: product.sales,
    })) || [];

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip bg-white p-3 border rounded shadow-lg">
          <p className="font-bold">{data.name}</p>
          <p>Sales: ₹ {data.value}</p>
          {data.name === "Others" && (
            <p className="text-sm text-gray-500">
              ({salesData.length - 10} products combined)
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom label function
  const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 10;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#656565"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fontSize: "11px",
          fontWeight: "semibold",
        }}
      >
        {value}
      </text>
    );
  };

  const handleSales = () => {
    // navigate("SalesVsProducts");
  };

  // Handle responsive sizing
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 768) {
        //FIXME: mobile size
        // Mobile screens
        setOuterRadiusChart(140);
        setContainerHeight(500);
      } else {
        // Desktop screens
        setOuterRadiusChart(210);
        setContainerHeight(600);
      }
    };

    handleResize(); // Initialize sizes
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!salesData || salesData.length === 0) {
    return (
      <div className="border border-gray-100 rounded-md p-5 text-center min-h-[600px]">
        <h1 className="text-text1 text-lg font-semibold mb-4">
          Product Sales Distribution
        </h1>
        <Loading />
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-md p-5 min-h-[550px] sm:min-h-[650px]">
      <div
        className="flex items-center gap-2 mb-2 cursor-pointer"
        onClick={handleSales}
      >
        <h1 className="text-gray-700 text-lg font-semibold">
          Sales vs Product Groups
        </h1>
        <ExternalLink className="text-gray-700 w-5 h-5 items-center" />
      </div>

      <div className="flex items-center justify-center">
        <ResponsiveContainer width="100%" height={containerHeight}>
          <PieChart>
            <Pie
              data={processedPieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={outerRadiusChart}
              dataKey="value"
              label={renderCustomizedLabel}
            >
              {processedPieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconSize={10}
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              iconType="rect"
              formatter={(value) => (
                <span style={{ marginRight: "20px" }}>{value}</span>
              )}
              wrapperStyle={{
                fontSize: "14px",
                gap: 20,
                paddingTop: "10px",
                marginTop: "10px",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SalesPieChart;
